import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { fetchCatBrands, type PrevBrandItem } from '@/api/catBrand';
import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyContentPanelInset, SurveyQuestionPanel } from '@/components/layout/SurveyContentPanel';
import PrevBrandPicker, {
  formatPrevBrandSelection,
  isPrevBrandSelectionValid,
} from '@/components/survey/PrevBrandPicker';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import {
  PREV_BRAND_OTHERS,
  buildPrevBrandAnswers,
  readPrevBrandAnswers,
  writePrevBrandAnswers,
} from '@/lib/prevBrandStorage';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';

export default function PrevBrand() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [brands, setBrands] = useState<PrevBrandItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState<string | null>(null);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState(() => readPrevBrandAnswers()?.brand ?? '');
  const [selectedVariant, setSelectedVariant] = useState(() => readPrevBrandAnswers()?.variant ?? '');

  useEffect(() => {
    writePrevBrandAnswers({ brand: selectedBrand, variant: selectedVariant });
  }, [selectedBrand, selectedVariant]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setWarning(null);
    void (async () => {
      try {
        const list = await fetchCatBrands();
        if (cancelled) return;
        setBrands(list);
      } catch {
        if (!cancelled) setWarning(t.prevBrand.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t.prevBrand.loadError]);

  useEffect(() => {
    if (selectedBrand && selectedBrand !== PREV_BRAND_OTHERS && !expandedBrand) {
      setExpandedBrand(selectedBrand);
    }
  }, [selectedBrand, expandedBrand]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet === 'B') return <Navigate to="/ldproductnow" replace />;

  const handleSelectVariant = (brand: string, variant: string) => {
    setSelectedBrand(brand);
    setSelectedVariant(variant);
    setWarning(null);
  };

  const handleOtherSpecifyChange = (value: string) => {
    const normalized = buildPrevBrandAnswers(PREV_BRAND_OTHERS, value);
    setSelectedBrand(normalized.brand);
    setSelectedVariant(normalized.variant);
    setExpandedBrand(null);
    setWarning(null);
  };

  const handleContinue = () => {
    if (selectedBrand === PREV_BRAND_OTHERS && !selectedVariant.trim()) {
      setWarning(t.prevBrand.otherSpecifyWarning);
      return;
    }
    if (!isPrevBrandSelectionValid(selectedBrand, selectedVariant)) {
      setWarning(t.prevBrand.requiredWarning);
      return;
    }
    writePrevBrandAnswers(buildPrevBrandAnswers(selectedBrand, selectedVariant));
    navigate('/survey/prevbrandanswer');
  };

  const hasSelection = isPrevBrandSelectionValid(selectedBrand, selectedVariant);

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto mt-[7vh] flex min-h-0 w-full max-w-full flex-1 flex-col">
          <SurveyContentPanelInset className="shrink-0">
            <SurveyQuestionPanel className="!mb-4">
              <p className="m-0 break-words text-left text-[19px] font-bold text-white sm:text-[20px]">
                {t.prevBrand.question}
              </p>
            </SurveyQuestionPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <PrevBrandPicker
              brands={brands}
              loading={loading}
              expandedBrand={expandedBrand}
              selectedBrand={selectedBrand}
              selectedVariant={selectedVariant}
              onExpandedBrandChange={setExpandedBrand}
              onSelectVariant={handleSelectVariant}
              onOtherSpecifyChange={handleOtherSpecifyChange}
            />
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {hasSelection ? (
                <p className="m-0 text-center text-sm font-bold text-white/90">
                  {formatPrevBrandSelection(selectedBrand, selectedVariant, t.prevBrand.otherSpecify)}
                </p>
              ) : null}
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton disabled={loading} onClick={handleContinue}>
                {t.prevBrand.continue}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
